import { FloorMovingMethodTypeDto, PlaceDoorDirectionTypeDto, StairHeightLevel, StairInfo, } from '@/generated-sources/openapi';
export const FLOOR_MOVING_METHOD_LABELS = {
    [FloorMovingMethodTypeDto.PlaceElevator]: '매장 엘리베이터',
    [FloorMovingMethodTypeDto.PlaceStairs]: '매장 계단',
    [FloorMovingMethodTypeDto.PlaceEscalator]: '매장 에스컬레이터',
    [FloorMovingMethodTypeDto.BuildingElevator]: '건물 엘리베이터',
    [FloorMovingMethodTypeDto.BuildingStairs]: '건물 계단',
    [FloorMovingMethodTypeDto.BuildingEscalator]: '건물 에스컬레이터',
    [FloorMovingMethodTypeDto.Unknown]: '기타',
};
export function getAccessibilitySections(params) {
    const { isStandalone, doorDir, isMultiFloor, hasV2Fields, hasBuildingAccessibility: _hasBuildingAccessibility, showBuildingEntranceForOutsideDoor = false, } = params;
    const sections = ['층 정보'];
    const isInsideDoor = !isStandalone && doorDir === PlaceDoorDirectionTypeDto.InsideBuilding;
    if (hasV2Fields) {
        // InsideBuilding: 건물 출입구 → 매장 출입구 순서
        if (isInsideDoor) {
            sections.push('건물 출입구');
        }
        sections.push('매장 출입구');
        if (isMultiFloor)
            sections.push('층간 이동 정보');
        // OutsideBuilding: 매장 출입구 뒤, 내부 이용 정보 직전 (접근성 탭에서만)
        if (showBuildingEntranceForOutsideDoor && !isStandalone && !isInsideDoor) {
            sections.push('건물 출입구');
        }
        sections.push('내부 이용 정보');
    }
    else {
        if (isInsideDoor)
            sections.push('건물 출입구');
        sections.push('매장 출입구');
        if (showBuildingEntranceForOutsideDoor && !isInsideDoor)
            sections.push('건물 출입구');
        sections.push('내부 이용 정보');
    }
    return sections;
}
export var FloorAccessibilityType;
(function (FloorAccessibilityType) {
    FloorAccessibilityType[FloorAccessibilityType["GroundFloor"] = 0] = "GroundFloor";
    FloorAccessibilityType[FloorAccessibilityType["GroundAndMoreFloors"] = 1] = "GroundAndMoreFloors";
    FloorAccessibilityType[FloorAccessibilityType["GroundAndMoreFloorsWithStairOnly"] = 2] = "GroundAndMoreFloorsWithStairOnly";
    FloorAccessibilityType[FloorAccessibilityType["UpperWithElevator"] = 3] = "UpperWithElevator";
    FloorAccessibilityType[FloorAccessibilityType["UpperWithoutElevator"] = 4] = "UpperWithoutElevator";
    FloorAccessibilityType[FloorAccessibilityType["UndergroundWithElevator"] = 5] = "UndergroundWithElevator";
    FloorAccessibilityType[FloorAccessibilityType["UndergroundWithoutElevator"] = 6] = "UndergroundWithoutElevator";
    FloorAccessibilityType[FloorAccessibilityType["UnknownButNotOnGround"] = 7] = "UnknownButNotOnGround";
    FloorAccessibilityType[FloorAccessibilityType["Unknown"] = 8] = "Unknown";
})(FloorAccessibilityType || (FloorAccessibilityType = {}));
export function getFloorAccessibility(accessibility, doorDirectionType) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const floors = (_b = (_a = accessibility.placeAccessibility) === null || _a === void 0 ? void 0 : _a.floors) !== null && _b !== void 0 ? _b : [];
    const isSingleFloor = floors.length === 1;
    const floorName = floors[0] < 1 ? `지하 ${-floors[0]}층` : `${floors[0]}층`;
    const isStandaloneBuilding = ((_c = accessibility.placeAccessibility) === null || _c === void 0 ? void 0 : _c.isStandaloneBuilding) === true;
    // 과거 등록 데이터 : 층수 정보가 없는 경우
    if (floors.length === 0) {
        // 1층인 경우와 1층이 아닌 경우로 구분 가능
        if ((_d = accessibility.placeAccessibility) === null || _d === void 0 ? void 0 : _d.isFirstFloor) {
            return {
                type: FloorAccessibilityType.GroundFloor,
                title: '1층',
            };
        }
        return {
            type: FloorAccessibilityType.UnknownButNotOnGround,
            title: '1층 아님',
        };
    }
    if (isSingleFloor && floors[0] === 1) {
        return {
            type: FloorAccessibilityType.GroundFloor,
            title: isStandaloneBuilding ? '단독 1층 건물' : '1층',
        };
    }
    if (!isSingleFloor) {
        const methods = (_f = (_e = accessibility.placeAccessibility) === null || _e === void 0 ? void 0 : _e.floorMovingMethodTypes) !== null && _f !== void 0 ? _f : [];
        const stairOnlyTypes = [
            FloorMovingMethodTypeDto.PlaceStairs,
            FloorMovingMethodTypeDto.BuildingStairs,
        ];
        const isStairOnly = methods.length > 0 && methods.every(m => stairOnlyTypes.includes(m));
        let description;
        if (isStairOnly) {
            description = '계단으로만 이동 가능';
        }
        else if (methods.length > 0) {
            description = methods.map(m => FLOOR_MOVING_METHOD_LABELS[m]).join(', ');
        }
        else {
            // fallback for V1 data
            description = ((_g = accessibility.placeAccessibility) === null || _g === void 0 ? void 0 : _g.isStairOnlyOption)
                ? '계단으로만 이동 가능'
                : '계단 외 이동 방법 있음';
        }
        const multiFloorTitle = isStandaloneBuilding
            ? '단독 건물 여러층'
            : '1층을 포함한 여러층';
        if (isStairOnly ||
            (methods.length === 0 &&
                ((_h = accessibility.placeAccessibility) === null || _h === void 0 ? void 0 : _h.isStairOnlyOption))) {
            return {
                type: FloorAccessibilityType.GroundAndMoreFloorsWithStairOnly,
                title: multiFloorTitle,
                description,
            };
        }
        else {
            return {
                type: FloorAccessibilityType.GroundAndMoreFloors,
                title: multiFloorTitle,
                description,
            };
        }
    }
    const isOutsideBuilding = doorDirectionType === PlaceDoorDirectionTypeDto.OutsideBuilding;
    if (isSingleFloor && floors[0] > 1) {
        // 건물 밖에 문이 있으면 건물 엘리베이터는 접근에 무관하므로 BA를 참고하지 않는다.
        const canUseBuildingElevator = !isOutsideBuilding && ((_j = accessibility.buildingAccessibility) === null || _j === void 0 ? void 0 : _j.hasElevator);
        if (canUseBuildingElevator) {
            return {
                type: FloorAccessibilityType.UpperWithElevator,
                title: floorName,
                description: '엘리베이터로 이동 가능',
            };
        }
        else {
            return {
                type: FloorAccessibilityType.UpperWithoutElevator,
                title: floorName,
                description: accessibility.buildingAccessibility === undefined &&
                    !isOutsideBuilding
                    ? '엘레베이터 정보가 필요해요'
                    : accessibility.buildingAccessibility === undefined ||
                        isOutsideBuilding
                        ? undefined
                        : '계단으로만 이동 가능',
            };
        }
    }
    if (isSingleFloor && floors[0] < 1) {
        const canUseBuildingElevator = !isOutsideBuilding && ((_k = accessibility.buildingAccessibility) === null || _k === void 0 ? void 0 : _k.hasElevator);
        if (canUseBuildingElevator) {
            return {
                type: FloorAccessibilityType.UndergroundWithElevator,
                title: floorName,
                description: '엘리베이터로 이동 가능',
            };
        }
        else {
            return {
                type: FloorAccessibilityType.UndergroundWithoutElevator,
                title: floorName,
                description: accessibility.buildingAccessibility === undefined &&
                    !isOutsideBuilding
                    ? '엘레베이터 정보가 필요해요'
                    : accessibility.buildingAccessibility === undefined ||
                        isOutsideBuilding
                        ? undefined
                        : '계단으로만 이동 가능',
            };
        }
    }
    return {
        type: FloorAccessibilityType.Unknown,
        title: '알 수 없음',
    };
}
export var EntranceStepType;
(function (EntranceStepType) {
    EntranceStepType[EntranceStepType["StairOnly"] = 0] = "StairOnly";
    EntranceStepType[EntranceStepType["SlopeOnly"] = 1] = "SlopeOnly";
    EntranceStepType[EntranceStepType["StairAndSlope"] = 2] = "StairAndSlope";
    EntranceStepType[EntranceStepType["Flat"] = 3] = "Flat";
    EntranceStepType[EntranceStepType["Unknown"] = 4] = "Unknown";
})(EntranceStepType || (EntranceStepType = {}));
export function getPlaceEntranceStepType(pa) {
    const stairInfo = pa.stairInfo;
    const hasSlope = pa.hasSlope;
    if (stairInfo === StairInfo.None) {
        if (hasSlope) {
            return EntranceStepType.SlopeOnly;
        }
        else {
            return EntranceStepType.Flat;
        }
    }
    else {
        if (hasSlope) {
            return EntranceStepType.StairAndSlope;
        }
        else {
            return EntranceStepType.StairOnly;
        }
    }
}
export function getBuildingEntranceStepType(ba) {
    const stairInfo = ba.entranceStairInfo;
    const hasSlope = ba.hasSlope;
    if (stairInfo === StairInfo.None) {
        if (hasSlope) {
            return EntranceStepType.SlopeOnly;
        }
        else {
            return EntranceStepType.Flat;
        }
    }
    else {
        if (hasSlope) {
            return EntranceStepType.StairAndSlope;
        }
        else {
            return EntranceStepType.StairOnly;
        }
    }
}
export var ElevatorType;
(function (ElevatorType) {
    ElevatorType[ElevatorType["ElevatorAfterStair"] = 0] = "ElevatorAfterStair";
    ElevatorType[ElevatorType["ElevatorNoBarriers"] = 1] = "ElevatorNoBarriers";
    ElevatorType[ElevatorType["NoElevator"] = 2] = "NoElevator";
})(ElevatorType || (ElevatorType = {}));
export function getBuildingElevatorType(ba) {
    const hasElevator = ba.hasElevator;
    const stairInfo = ba.elevatorStairInfo;
    const hasSlope = ba.elevatorHasSlope;
    if (!hasElevator) {
        return ElevatorType.NoElevator;
    }
    // 계단 없고 경사로도 없는 경우 (null은 하위호환 — 경사로 없음으로 취급)
    if (stairInfo === StairInfo.None && !hasSlope) {
        return ElevatorType.ElevatorNoBarriers;
    }
    return ElevatorType.ElevatorAfterStair;
}
export function getStairDescription(height, stair) {
    const description = [
        getStairHeightDescription(height),
        getStairCountDescription(stair),
    ]
        .filter(e => e)
        .join(', ');
    return description;
}
function getStairHeightDescription(height) {
    switch (height) {
        case StairHeightLevel.HalfThumb:
            return '엄지 한마디 높이';
        case StairHeightLevel.Thumb:
            return '엄지 손가락 높이';
        case StairHeightLevel.OverThumb:
            return '엄지 손가락 이상 높이';
        default:
            return '';
    }
}
function getStairCountDescription(stair) {
    switch (stair) {
        case StairInfo.One:
            return '1칸';
        case StairInfo.TwoToFive:
            return '2-5칸';
        case StairInfo.OverSix:
            return '6칸 이상';
        default:
            return '';
    }
}
